import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Users, UserPlus, RefreshCw, Shuffle, User, CheckCircle, 
  ArrowRight, Settings, BarChart3, Target, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadAssignment = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  
  // Check if user can assign leads - only Kheldoune or Admin
  const allowedAssignmentEmails = [
    'z.kheldoune@fakhruddinproperties.com',
    'admin@treppanliving.ae'
  ];
  const canAssignLeads = user?.email && (
    allowedAssignmentEmails.includes(user.email.toLowerCase()) ||
    ['super_admin', 'admin', 'owner', 'account_owner'].includes(user.role?.toLowerCase().replace(' ', '_'))
  );
  
  // Data state
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [roundRobinConfig, setRoundRobinConfig] = useState(null);
  const [unassignedContacts, setUnassignedContacts] = useState([]);
  
  // UI state
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showManualAssignDialog, setShowManualAssignDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [contactToAssign, setContactToAssign] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Progressive — each panel paints as it arrives.
  const fetchData = () => {
    setLoading(true);
    const tasks = [
      axios.get(`${API}/lead-assignment/stats?token=${token}`)
        .then((r) => {
          if (r.data.success) {
            setStats(r.data);
            setAgents(r.data.agents || []);
          }
        })
        .catch((e) => console.error('[LeadAssignment] stats failed:', e)),
      axios.get(`${API}/lead-assignment/round-robin?token=${token}`)
        .then((r) => r.data.success && setRoundRobinConfig(r.data.config))
        .catch((e) => console.error('[LeadAssignment] round-robin failed:', e)),
      axios.get(`${API}/contacts?token=${token}`)
        .then((r) => {
          const contacts = r.data || [];
          setUnassignedContacts(contacts.filter((c) => !c.assigned_to));
        })
        .catch((e) => console.error('[LeadAssignment] contacts failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const handleToggleRoundRobin = async (enabled) => {
    try {
      await axios.put(`${API}/lead-assignment/round-robin?token=${token}`, {
        is_enabled: enabled
      });
      setRoundRobinConfig(prev => ({ ...prev, is_enabled: enabled }));
      toast.success(enabled ? 'Round-robin enabled' : 'Round-robin disabled');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleAssignSingle = async (contactId) => {
    setAssigning(true);
    try {
      const response = await axios.post(`${API}/lead-assignment/assign-round-robin?token=${token}`, {
        contact_id: contactId
      });
      
      if (response.data.success) {
        toast.success(`Lead assigned to ${response.data.assigned_to?.name || 'agent'}`);
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to assign lead');
      }
    } catch (error) {
      toast.error('Failed to assign lead');
    } finally {
      setAssigning(false);
    }
  };

  const handleBulkAssign = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Please select contacts to assign');
      return;
    }
    
    setAssigning(true);
    try {
      const response = await axios.post(`${API}/lead-assignment/bulk-assign?token=${token}`, {
        contact_ids: selectedContacts
      });
      
      if (response.data.success) {
        toast.success(`Assigned ${response.data.assigned} leads using round-robin`);
        setSelectedContacts([]);
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to assign leads');
      }
    } catch (error) {
      toast.error('Failed to assign leads');
    } finally {
      setAssigning(false);
    }
  };

  const handleManualAssign = async () => {
    if (!contactToAssign || !selectedAgent) {
      toast.error('Please select a contact and agent');
      return;
    }
    
    setAssigning(true);
    try {
      const response = await axios.post(`${API}/lead-assignment/assign?token=${token}`, {
        contact_id: contactToAssign.id,
        assigned_to: selectedAgent
      });
      
      if (response.data.success) {
        const agent = agents.find(a => a.id === selectedAgent);
        toast.success(`Lead assigned to ${agent?.name || 'agent'}`);
        setShowManualAssignDialog(false);
        setContactToAssign(null);
        setSelectedAgent('');
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to assign lead');
      }
    } catch (error) {
      toast.error('Failed to assign lead');
    } finally {
      setAssigning(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === unassignedContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(unassignedContacts.map(c => c.id));
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  // If user doesn't have permission, show access denied message
  if (!canAssignLeads) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center" data-testid="lead-assignment-denied">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
        <p className="text-slate-500 max-w-md">
          Only authorized users (Kheldoune or Admin) can assign leads. 
          Please contact your administrator if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-assignment-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-primary">Lead Assignment</h1>
          <p className="text-slate-500">Distribute leads to your sales team using round-robin</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Round-Robin</span>
            <Switch
              checked={roundRobinConfig?.is_enabled}
              onCheckedChange={handleToggleRoundRobin}
              data-testid="round-robin-switch"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Contacts</p>
                <p className="text-2xl font-bold">{stats?.total_contacts || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="text-blue-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Assigned</p>
                <p className="text-2xl font-bold text-green-600">{stats?.total_assigned || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="text-green-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Unassigned</p>
                <p className="text-2xl font-bold text-amber-600">{stats?.unassigned || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="text-amber-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Team Members</p>
                <p className="text-2xl font-bold">{agents.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Target className="text-purple-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Members Distribution */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 size={20} />
              Lead Distribution
            </CardTitle>
            <CardDescription>Leads assigned to each team member</CardDescription>
          </CardHeader>
          <CardContent>
            {agents.length === 0 ? (
              <p className="text-center py-4 text-slate-500">No team members found</p>
            ) : (
              <div className="space-y-3">
                {agents.map((agent) => {
                  const percentage = stats?.total_assigned > 0 
                    ? Math.round((agent.assigned_leads / stats.total_assigned) * 100) 
                    : 0;
                  
                  return (
                    <div key={agent.id} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                            <User size={14} className="text-accent" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{agent.name}</p>
                            <p className="text-xs text-slate-500 capitalize">{agent.role}</p>
                          </div>
                        </div>
                        <Badge variant="secondary">{agent.assigned_leads}</Badge>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-accent h-2 rounded-full transition-all" 
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Unassigned Leads */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus size={20} />
                  Unassigned Leads ({unassignedContacts.length})
                </CardTitle>
                <CardDescription>Select leads to assign using round-robin</CardDescription>
              </div>
              {selectedContacts.length > 0 && (
                <Button 
                  onClick={handleBulkAssign}
                  disabled={assigning}
                  className="bg-accent hover:bg-accent/90"
                  data-testid="bulk-assign-btn"
                >
                  {assigning ? (
                    <RefreshCw className="mr-2 animate-spin" size={16} />
                  ) : (
                    <Shuffle className="mr-2" size={16} />
                  )}
                  Assign {selectedContacts.length} Leads
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {unassignedContacts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="mx-auto mb-2 text-green-500" size={32} />
                <p>All leads have been assigned!</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                <div className="flex items-center gap-2 pb-2 border-b sticky top-0 bg-white">
                  <Checkbox
                    checked={selectedContacts.length === unassignedContacts.length}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                  <span className="text-sm text-slate-500">Select All</span>
                </div>
                
                {unassignedContacts.slice(0, 50).map((contact) => (
                  <div 
                    key={contact.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-300"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedContacts.includes(contact.id)}
                        onCheckedChange={() => toggleContactSelection(contact.id)}
                      />
                      <div>
                        <p className="font-medium">
                          {contact.first_name} {contact.last_name}
                        </p>
                        <p className="text-sm text-slate-500">{contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {contact.lead_score && (
                        <Badge variant="outline">Score: {contact.lead_score}</Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAssignSingle(contact.id)}
                        disabled={assigning}
                        title="Assign using round-robin"
                      >
                        <Shuffle size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setContactToAssign(contact);
                          setShowManualAssignDialog(true);
                        }}
                        title="Manual assign"
                      >
                        <ArrowRight size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {unassignedContacts.length > 50 && (
                  <p className="text-center text-sm text-slate-500 py-2">
                    Showing 50 of {unassignedContacts.length} unassigned leads
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manual Assign Dialog */}
      <Dialog open={showManualAssignDialog} onOpenChange={setShowManualAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Lead Manually</DialogTitle>
            <DialogDescription>
              Select a team member to assign this lead to
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {contactToAssign && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">
                  {contactToAssign.first_name} {contactToAssign.last_name}
                </p>
                <p className="text-sm text-slate-500">{contactToAssign.email}</p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium mb-2 block">Assign to</label>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger data-testid="agent-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} ({agent.assigned_leads} leads)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowManualAssignDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleManualAssign}
                disabled={assigning || !selectedAgent}
                className="bg-accent hover:bg-accent/90"
                data-testid="confirm-assign-btn"
              >
                {assigning ? (
                  <RefreshCw className="mr-2 animate-spin" size={16} />
                ) : (
                  <CheckCircle className="mr-2" size={16} />
                )}
                Assign Lead
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadAssignment;
